import { Vendor } from "pages/admin/experiments/cropExtraction/types";

// Canonical NC-code → German crop name mapping. This is the authoritative
// reference used across all parsers; vendor-specific overrides live in
// `REGIONAL` below.
const COMMON: Record<string, string> = {
  "10": "Zuckermais",
  "20": "Koppelschafweiden",
  "49": "Unbestockte Obstbaufläche",
  "70": "Hecken / Knicks (>10m)",
  "71": "Baumreihe (>50m)",
  "72": "Feldgehölz (50–2000 m²)",
  "73": "Feuchtgebiet (<2000 m²)",
  "74": "Einzelbaum",
  "75": "Tümpel / Sölle / Doline",
  "76": "Natur-, Stein- oder Trockenmauer",
  "77": "Fels- und Steinriegel / naturbelassene Fläche",
  "78": "Feldraine",
  "83": "Agroforststreifen ohne ÖR",
  "88": "ÖR 1a Brache (Selbst-/Begrünung)",
  "89": "ÖR 1a Brache (aktive Begrünung)",
  "90": "ÖR 1b Blühfläche auf AL",
  "92": "ÖR 1c Blühfläche auf DK",
  "93": "ÖR 1d Altgrasstreifen",
  "94": "ÖR 3 Agroforststreifen",
  "112": "Winterdurum (Hartweizen)",
  "113": "Sommerdurum (Hartweizen)",
  "114": "Winter-Dinkel",
  "115": "Winterweichweizen",
  "116": "Sommerweichweizen",
  "118": "Winter-Emmer/-Einkorn",
  "119": "Sommer-Emmer/-Einkorn",
  "120": "Sommer-Dinkel",
  "121": "Winterroggen",
  "122": "Sommerroggen",
  "125": "Wintermenggetreide",
  "126": "Wintermenggetreide ohne Weizen",
  "131": "Wintergerste",
  "132": "Sommergerste",
  "142": "Winterhafer",
  "143": "Sommerhafer",
  "144": "Sommermenggetreide",
  "145": "Sommermenggetreide ohne Weizen",
  "150": "Gemenge Getreide/Leguminose (Getreide überwiegt)",
  "156": "Wintertriticale",
  "157": "Sommertriticale",
  "171": "Mais (ohne Silomais)",
  "181": "Rispenhirse",
  "182": "Buchweizen",
  "183": "Mohren-/Zuckerhirse",
  "184": "Kolbenhirse",
  "186": "Amarant",
  "187": "Quinoa",
  "188": "Reis im Trockenanbau",
  "189": "Chia",
  "190": "Getreide sonstige",
  "210": "Erbsen",
  "211": "Gemüseerbse",
  "212": "Platterbse",
  "220": "Ackerbohne",
  "221": "Wicken",
  "222": "Linsen",
  "230": "Lupinen",
  "240": "Erbsen/Bohnen Gemenge",
  "250": "Gemenge Leguminose/Getreide (Leguminose überwiegt)",
  "290": "Hülsenfrucht sonstige",
  "311": "Winterraps",
  "312": "Sommerraps",
  "315": "Winterrübsen",
  "316": "Sommerrübsen",
  "317": "Ölrettich",
  "320": "Sonnenblumen",
  "330": "Sojabohnen",
  "341": "Lein (Flachs)",
  "390": "Ölfrucht sonstige",
  "392": "Meerkohl/Krambe",
  "393": "Leindotter",
  "410": "Mais mit Leguminosen",
  "411": "Silomais",
  "413": "Futterrübe/Runkelrübe",
  "414": "Kohlrübe/Steckrübe",
  "421": "Klee",
  "422": "Kleegras",
  "423": "Luzerne",
  "424": "Ackergras",
  "425": "Klee-Luzerne-Gemisch",
  "426": "Bockshornklee",
  "427": "Hornklee",
  "429": "Esparsette",
  "430": "Serradella",
  "431": "Steinklee",
  "432": "Kleemischung",
  "433": "Luzerne-Gras",
  "434": "Gras-Leguminosen-Gemisch",
  "441": "Grünlandeinsaat – Wiesen",
  "442": "Grünlandeinsaat – Mähweiden",
  "443": "Grünlandeinsaat – Weiden",
  "451": "Wiesen",
  "452": "Mähweiden",
  "453": "Weiden",
  "454": "Hutungen",
  "455": "Almen/Alpen",
  "458": "Streuwiesen",
  "460": "Sommerweiden",
  "480": "Streuobstfläche mit Grünlandnutzung",
  "481": "Streuobst ohne Wiesennutzung",
  "492": "Dauergrünland lokale Praktiken",
  "512": "Iberischer Drachenkopf",
  "545": "Stillgelegte Ackerflächen",
  "546": "Stillgelegte Dauergrünlandflächen",
  "563": "Stillgelegte Ackerflächen LPR",
  "564": "Aufforstung",
  "567": "Stillgelegtes Dauergrünland AUM",
  "575": "Blühfläche",
  "583": "Nicht landwirtschaftliche förderfähige Fläche",
  "584": "Natura 2000 Fläche",
  "585": "WRRL Fläche",
  "586": "Paludikultur",
  "590": "Brache mit Blühmischung",
  "591": "Ackerland aus Erzeugung genommen",
  "592": "Dauergrünland aus Erzeugung genommen",
  "593": "Dauerkultur aus Erzeugung genommen",
  "601": "Stärkekartoffeln",
  "602": "Speisekartoffeln",
  "603": "Zuckerrüben",
  "604": "Topinambur",
  "605": "Süßkartoffeln",
  "606": "Pflanzkartoffeln",
  "701": "Hanf",
  "702": "Rollrasen",
  "705": "Tabak",
  "706": "Mohn",
  "707": "Erdbeeren",
  "708": "Färberdistel",
  "709": "Brennnesseln",
  "777": "Phacelia",
  "802": "Silphium",
  "803": "Sudangras",
  "804": "Virginiamalve",
  "805": "Riesenweizengras",
  "821": "Kern- und Steinobst (Mischanbau)",
  "825": "Kernobst",
  "826": "Steinobst",
  "827": "Beerenobst",
  "829": "Sonstige Obstanlagen",
  "833": "Haselnüsse",
  "834": "Walnüsse",
  "835": "Sonstige Schalenfrüchte",
  "838": "Baumschulen",
  "839": "Beerenobst Vermehrung",
  "841": "Niederwald mit Kurzumtrieb",
  "844": "Unbestockte Rebflächen",
  "884": "Brache mit Rohboden",
  "885": "Mehrjährige Brache mit Verbot Mindesttätigkeit",
  "918": "Mehrjährige Blühmischung",
  "919": "Saatmais",
};

// Codes we've observed in real exports that aren't in the canonical NC-code
// list above. They survive here so the UI never shows "unmapped" for a code
// we already understand, but they're marked with a "(legacy)" suffix so the
// fact that they fall outside the canonical reference is obvious.
const LEGACY: Record<string, string> = {
  "450": "Grünland (legacy)",
  "459": "Sonstiges Grünland (legacy)",
  "912": "Hecken / Knicks (legacy code)",
  "981": "Feuchtgebiet (legacy code)",
  "994": "Einzelbaum (legacy code)",
  "995": "Baumgruppe (legacy code)",
  "996": "Stein-/Felsriegel (legacy code)",
};

// Vendor-specific overrides for codes that collide with COMMON or only exist
// inside one portal's schema (e.g. ELAN-RLP uses 41/88 for non-standard
// meanings; ELAN-SH uses single-letter codes).
const REGIONAL: Partial<Record<Vendor, Record<string, string>>> = {
  "elan-rlp": {
    "41": "Ackerland (sonstige Kulturen)",
    "105": "Körnermais",
    MS: "Silomais",
  },
  "elan-sh": {
    TC: "Triticale",
    W: "Weizen",
    G: "Gerste",
  },
};

const normalize = (code: string | number | undefined | null): string => {
  if (code === undefined || code === null) return "";
  const s = String(code).trim();
  if (!s) return "";
  const asNum = Number(s);
  if (!Number.isNaN(asNum) && /^\d+$/.test(s)) {
    return String(asNum);
  }
  return s.toUpperCase();
};

// Resolution order:
//   1. Vendor-specific override (if vendor is supplied)
//   2. Canonical NC-code list
//   3. Legacy observed codes
//   4. Any other vendor's overrides (last-ditch fallback)
export const describeCrop = (rawCode: string | number | undefined | null, vendor?: Vendor): string => {
  const code = normalize(rawCode);
  if (!code) return "";
  if (vendor && REGIONAL[vendor]?.[code]) return REGIONAL[vendor]![code];
  if (COMMON[code]) return COMMON[code];
  if (LEGACY[code]) return LEGACY[code];
  for (const overrides of Object.values(REGIONAL)) {
    if (overrides && overrides[code]) return overrides[code];
  }
  return "";
};

export const normalizeCropCode = normalize;
